import { useHasRole } from "contexts/AuthContext";
import { useTranslation } from "contexts/i18nContext";
import { useState } from "react";
import { ROLES } from "utils/roles";
import AddReporterModal from "./AddReporterModal";
import ReporterDetailModal from "./ReporterDetailModal";
import DocumentViewer from "components/DocumentViewer";
import { COMPLIANT_STATUS } from "utils/constantValues";

export default function ListReporterCard({
  complaintData,
  onComplaintsRefresh,
}: any) {
  const { t } = useTranslation();
  const canCreateReporter = useHasRole([
    ROLES.INSPECTION_DIRECTOR,
    ROLES.INSPECTION_COORDINATOR,
    ROLES.INSPECTION_PROSECUTOR,
  ]);

  const [isReporterOpen, setIsReporterOpen] = useState(false);
  const [selectedData, setSelectedData] = useState<any>(null);
  const [isCollapsed, setIsCollapsed] = useState(true); // closed by default

  return (
    <div className={isReporterOpen ? "min-h-screen" : ""}>
      <div className="bg-white rounded-xl p-4 shadow-sm">
        {/* Header with toggle and Add button */}
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-sm font-semibold text-gray-700">
            {t("reporter")}
          </h3>
          <div className="flex items-center gap-2">
            <button
              onClick={(e) => {
                e.stopPropagation();
                setIsCollapsed(!isCollapsed);
              }}
              className="p-1 hover:bg-gray-100 rounded"
            >
              {isCollapsed ? (
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              ) : (
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 15l7-7 7 7"
                  />
                </svg>
              )}
            </button>
            {canCreateReporter &&
              (complaintData?.status === COMPLIANT_STATUS.PENDING ||
                complaintData?.status ===
                  COMPLIANT_STATUS.UNDER_INVESTIGATION) && (
                <button
                  className="flex items-center gap-2 px-4 py-2 bg-red-800 hover:bg-red-700 text-white text-sm font-medium rounded-lg transition-colors"
                  onClick={() => setIsReporterOpen(true)}
                >
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 4v16m8-8H4"
                    />
                  </svg>
                  {t("addReporter")}
                </button>
              )}
          </div>
        </div>

        {/* Collapsible content */}
        {!isCollapsed && (
          <div className="space-y-2">
            {complaintData?.reporters_detail?.length === 0 ? (
              <div className="text-center py-8 border border-gray-300 rounded-lg bg-gray-50">
                <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-gray-200 flex items-center justify-center">
                  <span className="text-gray-500 text-2xl">📝</span>
                </div>
                <p className="text-gray-600 font-medium">
                  {t("noReportersAdded")}
                </p>
                <p className="text-sm text-gray-500 mt-1">
                  {canCreateReporter
                    ? t("addReportersToCase")
                    : t("noReportersRegistered")}
                </p>
              </div>
            ) : (
              complaintData?.reporters_detail?.map((obj: any) => (
                <div
                  key={obj?.uu_id}
                  className="group p-4 bg-white border border-gray-300 rounded-lg hover:border-red-300 hover:shadow-sm cursor-pointer transition-all overflow-hidden"
                  onClick={() => setSelectedData(obj)}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-3">
                      <div className="relative">
                        <div className="w-12 h-12 rounded-full bg-gray-800 border-2 border-white flex items-center justify-center">
                          <span className="text-lg font-bold text-yellow-500">
                            {obj?.name
                              ? obj.name
                                  .split(" ")
                                  .map((n: string) => n[0])
                                  .join("")
                                  .toUpperCase()
                                  .slice(0, 2)
                              : "R"}
                          </span>
                        </div>
                        <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-green-500 rounded-full border-2 border-white flex items-center justify-center">
                          <span className="text-xs text-white">✓</span>
                        </div>
                      </div>

                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <h4 className="text-base font-semibold text-gray-900 truncate">
                            {obj?.name || t("unnamedReporter")}
                          </h4>
                          <span className="px-2 py-0.5 bg-gray-100 text-gray-700 text-xs font-medium rounded-full">
                            {t("reporter")}
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="flex flex-col items-start gap-2">
                      <svg
                        className="w-5 h-5 text-yellow-500"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                        />
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                        />
                      </svg>
                    </div>
                  </div>

                  {/* ID Card Document */}
                  {obj?.id_card_documents &&
                    obj?.id_card_documents.length > 0 && (
                      <div className="mt-3 pt-3 border-t border-gray-100">
                        <div className="flex items-center gap-2 mb-2">
                          <svg
                            className="w-4 h-4 text-gray-500"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                            />
                          </svg>
                          <span className="text-sm font-medium text-gray-700">
                            {t("idDocument")}
                          </span>
                        </div>
                        <DocumentViewer
                          documents={obj?.id_card_documents}
                          baseUrl={`${process.env.REACT_APP_API_BASE_URL}prosecutor-decipiline/reporter-id-card-documents/document`}
                        />
                      </div>
                    )}
                </div>
              ))
            )}
          </div>
        )}
      </div>

      <AddReporterModal
        isOpen={isReporterOpen}
        setIsOpen={setIsReporterOpen}
        complaintData={complaintData}
        onRefresh={onComplaintsRefresh}
      />

      <ReporterDetailModal
        isOpen={!!selectedData}
        onClose={() => setSelectedData(null)}
        data={selectedData}
      />
    </div>
  );
}
